import { hash } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import * as tus from 'tus-js-client';
import { FileState, type Queue, UploadFile } from 'ember-file-upload';
import FileDropzone from 'ember-file-upload/components/file-dropzone';
import fileQueue from 'ember-file-upload/helpers/file-queue';
import type DataTransferWrapper from 'ember-file-upload/system/data-transfer-wrapper';
import { t } from 'ember-intl';
import { v4 as uuidv4 } from 'uuid';

import config from 'umg-ember-backend/config/environment';
import type CurrentUserService from 'umg-ember-backend/services/current-user';
import type FlashMessagesService from 'umg-ember-backend/services/flash-messages';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);
const apiConfig = config.API as { host: string };

declare module 'ember-file-upload' {
  interface UploadFile {
    uploadPath: string;
    uuid: string;
  }
}

interface FileUploaderSignature {
  Args: {
    disabledMessage?: string;
    hideOverlay?: boolean;
    isDisabled?: boolean;
    isMultiple?: boolean;
    onDragEnter?: (files: File[], dataTransfer: DataTransferWrapper) => void;
    onDragLeave?: (files: File[], dataTransfer: DataTransferWrapper) => void;
    onSuccess: (file: UploadFile) => void;
    onUploadStart?: (file: UploadFile) => void;
    onValidateUpload?: (file: File) => boolean;
    overlayMessage?: string;
  };
  Blocks: {
    default: [dropzone: { active: boolean; supported: boolean }, queue: Queue];
  };
  Element: HTMLElement;
}

async function uploadWithTus(
  file: UploadFile,
  metadata: NonNullable<tus.Upload['options']['metadata']>,
  onSuccess: (file: UploadFile) => void,
) {
  file.uuid = uuidv4();

  // Setup uploader
  const uploader = new tus.Upload(file.file, {
    chunkSize: 10e6,
    endpoint: `${apiConfig.host}/asset/files`,
    retryDelays: [1e3, 3e3, 5e3, 1e4, 3e4, 5e4, 1e5, 3e5, 5e5],
    removeFingerprintOnSuccess: true,
    storeFingerprintForResuming: true,
    metadata: {
      filename: file.name,
      filetype: file.type,
      ...metadata,
    },
    onProgress: (bytesUploaded, bytesTotal) => {
      file.loaded = bytesUploaded;
      file.size = bytesTotal;
      file.progress = Number(((bytesUploaded / bytesTotal) * 100).toFixed(2));
    },
    onSuccess: () => {
      const uploadKey = uploader.url!.split('/').pop()!;

      file.uploadPath = `${uploadKey}/${file.name}`;
      file.state = FileState.Uploaded;

      onSuccess(file);
    },
    onShouldRetry: (error) => {
      const status = error.originalResponse?.getStatus() ?? 0;

      if (status === 403) {
        return false;
      }

      return true;
    },
  });

  // Find previus upload and start
  const previousUploads = await uploader.findPreviousUploads();

  if (previousUploads.length) {
    uploader.resumeFromPreviousUpload(previousUploads[0]!);
  }

  uploader.start();

  file.state = FileState.Uploading;

  return uploader;
}

export default class UiFileUploaderComponent extends Component<FileUploaderSignature> {
  // Services
  @service declare currentUser: CurrentUserService;
  @service declare flashMessages: FlashMessagesService;

  // Getter and setter
  get uploaderId() {
    return `uploader-${guidFor(this)}`;
  }

  // Actions
  upload = (file: UploadFile) => {
    file.name = file.name.replace(/(..\/)|#|\?|:|\/|\\|&|"|'/g, '_');

    void uploadWithTus(
      file,
      {
        userId: this.currentUser.id,
      },
      this.args.onSuccess,
    );

    if (this.args.onUploadStart) {
      this.args.onUploadStart(file);
    }
  };

  validateFile = (file: File) => {
    if (this.args.isDisabled) {
      if (this.args.disabledMessage) {
        this.flashMessages.error(this.args.disabledMessage, {
          rawString: true,
        });
      }

      return false;
    }

    if (this.args.onValidateUpload) {
      return this.args.onValidateUpload(file);
    }

    return true;
  };

  // Template
  <template>
    {{#let (fileQueue name=this.uploaderId onFileAdded=this.upload) as |queue|}}
      <FileDropzone
        @filter={{this.validateFile}}
        @queue={{queue}}
        @multiple={{@isMultiple}}
        @cursor={{if @isDisabled 'none' 'copy'}}
        @onDragEnter={{@onDragEnter}}
        @onDragLeave={{@onDragLeave}}
        class={{(bem)}}
        ...attributes
        as |dropzone|
      >
        <div
          class={{bem
            'dropzone'
            (hash is-active=dropzone.active is-disabled=@isDisabled)
          }}
        >
          {{#unless @hideOverlay}}
            <div class={{bem 'overlay'}}>
              {{@overlayMessage}}
            </div>
          {{/unless}}

          {{#if (has-block)}}
            {{yield dropzone queue}}
          {{else}}
            <div class={{bem 'uploader'}}>
              {{#if @isDisabled}}
                {{@disabledMessage}}
              {{else}}
                {{#if queue.files.length}}
                  {{t 'ui.file_uploader.uploading' progress=queue.progress}}
                {{else if dropzone.supported}}
                  {{t 'ui.file_uploader.instruction'}}

                  <label for={{this.uploaderId}}>
                    <span
                      role='button'
                      tabindex='0'
                      aria-controls={{this.uploaderId}}
                      class={{bem 'select'}}
                    >
                      {{t 'ui.file_uploader.select_file'}}
                    </span>
                  </label>
                  <input
                    type='file'
                    id={{this.uploaderId}}
                    multiple={{@isMultiple}}
                    hidden
                    aria-hidden='true'
                    class={{bem 'input'}}
                    {{queue.selectFile filter=this.validateFile}}
                  />
                {{/if}}
              {{/if}}
            </div>
          {{/if}}
        </div>
      </FileDropzone>
    {{/let}}
  </template>
}
